import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';

import { Modal } from '#src/atoms/Modal/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { useUpdateOpenDialogContext } from '#src/contexts/dialogOpened/index.tsx';
import { useFlyTargetContext } from '#src/contexts/flyTarget/index.tsx';
import { useMousePositionContext } from '#src/contexts/mousePosition/index.tsx';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useTransitionApi } from '#src/hooks/ui/useTransitionApi.tsx';
import { useViewTransitionId } from '#src/hooks/ui/useViewTransitionId.ts';
import { useBodyLock } from '#src/hooks/useBodyLock.ts';
import { useShortcut } from '#src/hooks/useShortcut.ts';
import { thickMaterial } from '#src/style/utils.tsx';

import { CloseButton } from './CloseButton/index.tsx';
import { Header } from './Header/index.tsx';

const openingAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2%) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
}`;

const GlobalStyle = createGlobalStyle<{
  dialogRootName: string;
  dialogContentName: string;
}>`
  /** Backdrop default fadeIn transition */
  ::view-transition-group(${({ dialogRootName }) => {
    return dialogRootName;
  }}) {
    animation-duration: 0.15s;
  }
  ::view-transition-old(${({ dialogRootName }) => {
    return dialogRootName;
  }}) {
    animation-delay: 0.1s;
  }
  /** Disable backdrop filter during transition */
  ::view-transition-group(${({ dialogContentName }) => {
    return dialogContentName;
  }}) {
    backdrop-filter: none;
  }
  /** Opening animation */
  ::view-transition-new(${({ dialogContentName }) => {
    return dialogContentName;
  }}) {
    animation: .2s ease-out ${openingAnimation} forwards;
  }
  /** Closing animation */
  ::view-transition-old(${({ dialogContentName }) => {
    return dialogContentName;
  }}) {
    animation: .15s ease-out ${openingAnimation} reverse forwards;
  }
`;

const Root = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  overflow: auto;
  &:not(.fullScreen) {
    padding: var(--double-unit);
    background: var(--c-nl-900);
  }
  &.hideBackdrop {
    background: transparent;
    pointer-events: none;
  }
  &.blurBackdrop {
    ${thickMaterial}
  }
`;
const Content = styled(Vertical).attrs({ gap: 0 })`
  overflow: hidden; /* For Safari <= v15. The sticky footer won't work there */
  overflow: clip;
  border-radius: var(--triple-unit);
  border: 1px solid var(--c-nd-150);
  margin: auto;
  view-transition-name: dialog;
  background: var(--c-black);
  pointer-events: auto;

  &.fullScreen {
    width: var(--100vw);
    height: 100%;
    border: 0;
    border-radius: 0;
    max-width: none;
  }
  &.blurBackdrop {
    background: var(--c-nl-500);
  }
`;
const BodyWrapper = styled.div`
  position: relative;
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;
const Footer = styled.footer`
  position: sticky;
  bottom: calc(-1 * var(--double-unit));
  margin-top: auto;
  padding: 0 var(--double-unit) var(--double-unit);
  &.withDivider {
    border-top: 1px solid var(--c-nd-150);
  }
`;

export type Props = {
  className?: string;
  children?:
    | ((props: { CloseButton: typeof CloseButton }) => ReactNode)
    | ReactNode;
  title?: ReactNode;
  hideHeader?: boolean;
  footer?: ReactNode;
  open?: boolean;
  onBack?: () => void;
  onClose?: () => void;
  onClosed?: () => void;
  fullWidth?: boolean;
  fullScreen?: boolean;
  maxWidth?: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false;
  disableEscapeKeyDown?: boolean;
  disableBackdropClick?: boolean;
  style?: React.CSSProperties;
  withDivider?: boolean;
  hideBackdrop?: boolean;
  skipTransition?: boolean;
  blurBackdrop?: boolean;
};

const maxWidths = {
  xs: 444,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// Modal portals append to <body> in mount order: the last dialog is the visually topmost one.
const isTopmostDialog = (content: HTMLElement | null) => {
  const dialogs = document.querySelectorAll('[role="dialog"]');
  const topmost = dialogs[dialogs.length - 1];
  return !topmost || topmost === content;
};

// React can flush a close between two dialogs' listeners of the same keydown
// dispatch, making the dialog below topmost mid-event. The closing dialog
// claims the event so a single Escape press never closes both.
const handledEscapes = new WeakSet<KeyboardEvent>();

export const Dialog = ({
  className,
  children,
  title,
  hideHeader,
  footer,
  open,
  onBack,
  onClose,
  onClosed,
  fullWidth,
  fullScreen,
  maxWidth = 'xl',
  disableEscapeKeyDown,
  disableBackdropClick,
  style,
  withDivider,
  hideBackdrop = false,
  skipTransition = false,
  blurBackdrop,
}: Props) => {
  const isReadyRef = useRef(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const { updateDOM } = useTransitionApi();
  useShortcut(
    'Escape',
    e => {
      if (!open || disableEscapeKeyDown || handledEscapes.has(e)) {
        return;
      }
      if (isTopmostDialog(contentRef.current)) {
        handledEscapes.add(e);
        onClose?.();
      }
    },
    [open, onClose, disableEscapeKeyDown]
  );
  const { up: isLaptop } = useScreenSize('laptop');
  const isFullScreen = fullScreen ?? !isLaptop;
  const [isOpened, setIsOpened] = useState(isFullScreen ? open : false);
  const prevIsOpenedRef = useRef(isOpened);
  const dialogId = useViewTransitionId();
  const dialogRootName = `dialog-root-${dialogId}`;
  const dialogContentName = `dialog-content-${dialogId}`;
  const showHeader = !hideHeader && (onBack || title || onClose);

  const { handleMouseDown } = useMousePositionContext();
  const flyTarget = useFlyTargetContext();
  const rootRef = useRef<HTMLDivElement>(null);
  useBodyLock(isOpened);
  useUpdateOpenDialogContext(!!isOpened);

  const closing = isOpened && !open;
  const deferFullScreenClose =
    closing && isFullScreen && !skipTransition && !!flyTarget;

  if (
    isOpened !== open &&
    (isFullScreen || skipTransition) &&
    !deferFullScreenClose
  ) {
    setIsOpened(open);
  }

  useEffect(() => {
    if (isOpened === open || skipTransition || !isReadyRef.current) return;

    const useTransition = !isFullScreen || (closing && !!flyTarget);
    if (!useTransition) return;

    const flyingOut = !!(closing && flyTarget?.prepareFlyOut(rootRef.current));

    isReadyRef.current = false;
    const onTransitionEnd = () => {
      isReadyRef.current = true;
      flyTarget?.clearFlyOut();
      if (closing) onClosed?.();
    };
    updateDOM(
      () => {
        setIsOpened(open);
      },
      flyingOut ? ['mtt-fly-out'] : undefined
    ).finished.then(onTransitionEnd, onTransitionEnd);
  });

  useEffect(() => {
    const wasOpened = prevIsOpenedRef.current;
    prevIsOpenedRef.current = isOpened;
    if (wasOpened && !isOpened && isReadyRef.current) {
      onClosed?.();
    }
  }, [isOpened, onClosed]);

  return (
    <>
      {!skipTransition && (
        <GlobalStyle
          dialogRootName={dialogRootName}
          dialogContentName={dialogContentName}
        />
      )}
      {!!isOpened && (
        <Modal>
          <Root
            ref={rootRef}
            className={classNames({
              fullScreen: isFullScreen,
              hideBackdrop,
              blurBackdrop,
            })}
            style={{
              viewTransitionName: skipTransition ? undefined : dialogRootName,
            }}
            onClick={() => {
              if (!disableBackdropClick) {
                onClose?.();
              }
            }}
          >
            <Content
              ref={contentRef}
              role="dialog"
              className={classNames(className, {
                fullScreen: isFullScreen,
                blurBackdrop,
              })}
              style={{
                width: fullWidth ? '100%' : undefined,
                maxWidth:
                  maxWidth && !isFullScreen ? maxWidths[maxWidth] : undefined,
                viewTransitionName: dialogContentName,
                ...style,
              }}
              onMouseDown={e => {
                handleMouseDown(e);
                e.stopPropagation();
              }}
              onMouseUp={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
            >
              {!!showHeader && (
                <Header
                  onBack={onBack}
                  title={title}
                  onClose={onClose}
                  withDivider={withDivider}
                />
              )}
              <BodyWrapper>
                {typeof children === 'function'
                  ? children?.({ CloseButton })
                  : children}
              </BodyWrapper>
              {!!footer && (
                <Footer className={classNames({ withDivider })}>
                  {footer}
                </Footer>
              )}
            </Content>
          </Root>
        </Modal>
      )}
    </>
  );
};
